import type { HotkeyConfig } from "../types";

const listNavigationCache = new Map<string, Record<string, Omit<HotkeyConfig, "handler">>>();
const arrowNavigationCache = new Map<string, Record<string, Omit<HotkeyConfig, "handler">>>();
const tabShortcutsCache = new Map<number, Record<string, Omit<HotkeyConfig, "handler">>>();
const gridNavigationCache = new Map<string, Record<string, Omit<HotkeyConfig, "handler">>>();

const emailAppPatternsCache = new Map<string, Record<string, Omit<HotkeyConfig, "handler">>>();
let documentEditorPatternsCache: Record<string, Omit<HotkeyConfig, "handler">> | null = null;
const dataAppPatternsCache = new Map<string, Record<string, Omit<HotkeyConfig, "handler">>>();

export function clearPatternCaches(): void {
  listNavigationCache.clear();
  arrowNavigationCache.clear();
  tabShortcutsCache.clear();
  gridNavigationCache.clear();
  emailAppPatternsCache.clear();
  documentEditorPatternsCache = null;
  dataAppPatternsCache.clear();
}

export function getPatternCacheStats() {
  return {
    listNavigation: listNavigationCache.size,
    arrowNavigation: arrowNavigationCache.size,
    tabShortcuts: tabShortcutsCache.size,
    gridNavigation: gridNavigationCache.size,
    emailAppPatterns: emailAppPatternsCache.size,
    documentEditorPatterns: documentEditorPatternsCache ? 1 : 0,
    dataAppPatterns: dataAppPatternsCache.size,
  };
}

export const FILE_OPERATIONS: Readonly<Record<string, Omit<HotkeyConfig, "handler">>> = {
  NEW: {
    key: "n",
    modifiers: ["ctrl"],
    description: "New",
    priority: 90,
    category: "File",
  },
  OPEN: {
    key: "o",
    modifiers: ["ctrl"],
    description: "Open",
    priority: 85,
    category: "File",
  },
  SAVE: {
    key: "s",
    modifiers: ["ctrl"],
    description: "Save",
    priority: 95,
    category: "File",
  },
  SAVE_AS: {
    key: "s",
    modifiers: ["ctrl", "shift"],
    description: "Save As",
    priority: 75,
    category: "File",
  },
  PRINT: {
    key: "p",
    modifiers: ["ctrl"],
    description: "Print",
    priority: 80,
    category: "File",
  },
  CLOSE: {
    key: "w",
    modifiers: ["ctrl"],
    description: "Close",
    priority: 85,
    category: "File",
  },
} as const;

export const EDITING_OPERATIONS: Readonly<Record<string, Omit<HotkeyConfig, "handler">>> = {
  UNDO: {
    key: "z",
    modifiers: ["ctrl"],
    description: "Undo",
    priority: 100,
    category: "Edit",
  },
  REDO: {
    key: "y",
    modifiers: ["ctrl"],
    description: "Redo",
    priority: 100,
    category: "Edit",
  },
  CUT: {
    key: "x",
    modifiers: ["ctrl"],
    description: "Cut",
    priority: 100,
    category: "Edit",
  },
  COPY: {
    key: "c",
    modifiers: ["ctrl"],
    description: "Copy",
    priority: 100,
    category: "Edit",
  },
  PASTE: {
    key: "v",
    modifiers: ["ctrl"],
    description: "Paste",
    priority: 100,
    category: "Edit",
  },
  SELECT_ALL: {
    key: "a",
    modifiers: ["ctrl"],
    description: "Select All",
    priority: 95,
    category: "Edit",
  },
  FIND: {
    key: "f",
    modifiers: ["ctrl"],
    description: "Find",
    priority: 90,
    category: "Edit",
  },
  FIND_NEXT: {
    key: "g",
    modifiers: ["ctrl"],
    description: "Find Next",
    priority: 70,
    category: "Edit",
  },
  REPLACE: {
    key: "h",
    modifiers: ["ctrl"],
    description: "Replace",
    priority: 65,
    category: "Edit",
  },
} as const;

export const FORMATTING_OPERATIONS: Readonly<Record<string, Omit<HotkeyConfig, "handler">>> = {
  BOLD: {
    key: "b",
    modifiers: ["ctrl"],
    description: "Bold",
    priority: 80,
    category: "Format",
  },
  ITALIC: {
    key: "i",
    modifiers: ["ctrl"],
    description: "Italic",
    priority: 80,
    category: "Format",
  },
  UNDERLINE: {
    key: "u",
    modifiers: ["ctrl"],
    description: "Underline",
    priority: 80,
    category: "Format",
  },
  STRIKETHROUGH: {
    key: "s",
    modifiers: ["ctrl", "shift"],
    description: "Strikethrough",
    priority: 60,
    category: "Format",
  },
  INSERT_LINK: {
    key: "k",
    modifiers: ["ctrl"],
    description: "Insert Link",
    priority: 70,
    category: "Format",
  },
} as const;

export const NAVIGATION_SCOPED = {
  createListNavigation: (scope: string): Record<string, Omit<HotkeyConfig, "handler">> => {
    if (listNavigationCache.has(scope)) {
      return listNavigationCache.get(scope)!;
    }

    const patterns = {
      NEXT: {
        key: "j",
        scope,
        description: "Next item",
        priority: 70,
        category: "Navigation",
      },
      PREVIOUS: {
        key: "k",
        scope,
        description: "Previous item",
        priority: 70,
        category: "Navigation",
      },
      SELECT: {
        key: "x",
        scope,
        description: "Select item",
        priority: 65,
        category: "Navigation",
      },
      OPEN: {
        key: "o",
        scope,
        description: "Open item",
        priority: 65,
        category: "Navigation",
      },
      ENTER: {
        key: "Enter",
        scope,
        description: "Open/Activate item",
        priority: 75,
        category: "Navigation",
      },
    } as const;

    listNavigationCache.set(scope, patterns);
    return patterns;
  },

  createArrowNavigation: (scope: string): Record<string, Omit<HotkeyConfig, "handler">> => {
    if (arrowNavigationCache.has(scope)) {
      return arrowNavigationCache.get(scope)!;
    }

    const patterns = {
      UP: {
        key: "ArrowUp",
        scope,
        description: "Move up",
        priority: 85,
        category: "Navigation",
      },
      DOWN: {
        key: "ArrowDown",
        scope,
        description: "Move down",
        priority: 85,
        category: "Navigation",
      },
      LEFT: {
        key: "ArrowLeft",
        scope,
        description: "Move left",
        priority: 85,
        category: "Navigation",
      },
      RIGHT: {
        key: "ArrowRight",
        scope,
        description: "Move right",
        priority: 85,
        category: "Navigation",
      },
    } as const;

    arrowNavigationCache.set(scope, patterns);
    return patterns;
  },
} as const;

export const EMAIL_PATTERNS: Readonly<Record<string, Omit<HotkeyConfig, "handler">>> = {
  COMPOSE: {
    key: "c",
    modifiers: ["ctrl"],
    description: "Compose new email",
    priority: 85,
    category: "Email",
  },
  REPLY: {
    key: "r",
    modifiers: ["ctrl"],
    description: "Reply",
    priority: 88,
    category: "Email",
  },
  REPLY_ALL: {
    key: "r",
    modifiers: ["ctrl", "shift"],
    description: "Reply All",
    priority: 75,
    category: "Email",
  },
  FORWARD: {
    key: "f",
    modifiers: ["ctrl", "shift"],
    description: "Forward",
    priority: 75,
    category: "Email",
  },
  SEND: {
    key: "Enter",
    modifiers: ["ctrl"],
    description: "Send email",
    priority: 95,
    category: "Email",
  },
  ARCHIVE: {
    key: "e",
    modifiers: ["ctrl"],
    description: "Archive",
    priority: 65,
    category: "Email",
  },
  DELETE: {
    key: "d",
    modifiers: ["ctrl"],
    description: "Delete",
    priority: 60,
    category: "Email",
  },
  MARK_UNREAD: {
    key: "u",
    modifiers: ["ctrl"],
    description: "Mark as Unread",
    priority: 55,
    category: "Email",
  },
  MARK_SPAM: {
    key: "s",
    modifiers: ["ctrl", "shift"],
    description: "Mark as Spam",
    priority: 50,
    category: "Email",
  },
} as const;

export const MODAL_PATTERNS: Readonly<Record<string, Omit<HotkeyConfig, "handler">>> = {
  CLOSE: {
    key: "Escape",
    description: "Close modal",
    priority: 95,
    category: "Modal",
  },
  SUBMIT: {
    key: "Enter",
    modifiers: ["ctrl"],
    description: "Submit/Confirm",
    priority: 90,
    category: "Modal",
  },
  CANCEL: {
    key: "Escape",
    description: "Cancel",
    priority: 90,
    category: "Modal",
  },
} as const;

export const TAB_SWITCHING = {
  createTabShortcuts: (count: number = 9): Record<string, Omit<HotkeyConfig, "handler">> => {
    if (tabShortcutsCache.has(count)) {
      return tabShortcutsCache.get(count)!;
    }

    const shortcuts: Record<string, Omit<HotkeyConfig, "handler">> = {};

    for (let i = 1; i <= Math.min(count, 9); i++) {
      shortcuts[`TAB_${i}`] = {
        key: i.toString(),
        modifiers: ["ctrl"],
        description: `Go to tab ${i}`,
        priority: 60,
        category: "Navigation",
      };
    }

    shortcuts.TAB_LAST = {
      key: "9",
      modifiers: ["ctrl"],
      description: "Go to last tab",
      priority: 55,
      category: "Navigation",
    };

    tabShortcutsCache.set(count, shortcuts);
    return shortcuts;
  },
} as const;

export const MENU_PATTERNS: Readonly<Record<string, Omit<HotkeyConfig, "handler">>> = {
  FILE: {
    key: "f",
    modifiers: ["alt"],
    description: "File menu",
    priority: 60,
    category: "Menu",
  },
  EDIT: {
    key: "e",
    modifiers: ["alt"],
    description: "Edit menu",
    priority: 60,
    category: "Menu",
  },
  VIEW: {
    key: "v",
    modifiers: ["alt"],
    description: "View menu",
    priority: 60,
    category: "Menu",
  },
  HELP: {
    key: "h",
    modifiers: ["alt"],
    description: "Help menu",
    priority: 60,
    category: "Menu",
  },
} as const;

export const UNIVERSAL_PATTERNS: Readonly<Record<string, Omit<HotkeyConfig, "handler">>> = {
  HELP: {
    key: "?",
    description: "Show help",
    priority: 80,
    category: "Universal",
  },
  SEARCH: {
    key: "/",
    modifiers: ["ctrl"],
    description: "Search",
    priority: 85,
    category: "Universal",
  },
  COMMAND_PALETTE: {
    key: "k",
    modifiers: ["ctrl"],
    description: "Command Palette",
    priority: 85,
    category: "Universal",
  },
  SETTINGS: {
    key: ",",
    modifiers: ["ctrl"],
    description: "Settings",
    priority: 60,
    category: "Universal",
  },
} as const;

export const GRID_PATTERNS = {
  createGridNavigation: (scope: string): Record<string, Omit<HotkeyConfig, "handler">> => {
    if (gridNavigationCache.has(scope)) {
      return gridNavigationCache.get(scope)!;
    }

    const patterns = {
      CELL_UP: {
        key: "ArrowUp",
        scope,
        description: "Move to cell above",
        priority: 90,
        category: "Grid",
      },
      CELL_DOWN: {
        key: "ArrowDown",
        scope,
        description: "Move to cell below",
        priority: 90,
        category: "Grid",
      },
      CELL_LEFT: {
        key: "ArrowLeft",
        scope,
        description: "Move to cell left",
        priority: 90,
        category: "Grid",
      },
      CELL_RIGHT: {
        key: "ArrowRight",
        scope,
        description: "Move to cell right",
        priority: 90,
        category: "Grid",
      },
      EDIT_CELL: {
        key: "Enter",
        scope,
        description: "Edit cell",
        priority: 85,
        category: "Grid",
      },
      CANCEL_EDIT: {
        key: "Escape",
        scope,
        description: "Cancel edit",
        priority: 85,
        category: "Grid",
      },
      SELECT_ROW: {
        key: " ",
        scope,
        description: "Select row",
        priority: 75,
        category: "Grid",
      },
      DELETE_ROW: {
        key: "Delete",
        scope,
        description: "Delete row",
        priority: 65,
        category: "Grid",
      },
    } as const;

    gridNavigationCache.set(scope, patterns);
    return patterns;
  },
} as const;

export function createEmailAppPatterns(inboxScope: string = "inbox") {
  if (emailAppPatternsCache.has(inboxScope)) {
    return emailAppPatternsCache.get(inboxScope)!;
  }

  const patterns = {
    ...FILE_OPERATIONS,

    ...EMAIL_PATTERNS,

    ...NAVIGATION_SCOPED.createListNavigation(inboxScope),

    HELP: UNIVERSAL_PATTERNS.HELP,
    SEARCH: UNIVERSAL_PATTERNS.SEARCH,

    CLOSE_MODAL: MODAL_PATTERNS.CLOSE,
  };

  emailAppPatternsCache.set(inboxScope, patterns);
  return patterns;
}

export function createDocumentEditorPatterns() {
  if (documentEditorPatternsCache !== null) {
    return documentEditorPatternsCache;
  }

  const patterns = {
    ...FILE_OPERATIONS,

    ...EDITING_OPERATIONS,

    ...FORMATTING_OPERATIONS,

    ...MENU_PATTERNS,

    HELP: UNIVERSAL_PATTERNS.HELP,
    SETTINGS: UNIVERSAL_PATTERNS.SETTINGS,
  };

  documentEditorPatternsCache = patterns;
  return patterns;
}

export function createDataAppPatterns(gridScope: string = "grid") {
  if (dataAppPatternsCache.has(gridScope)) {
    return dataAppPatternsCache.get(gridScope)!;
  }

  const patterns = {
    SAVE: FILE_OPERATIONS.SAVE,
    PRINT: FILE_OPERATIONS.PRINT,

    UNDO: EDITING_OPERATIONS.UNDO,
    REDO: EDITING_OPERATIONS.REDO,
    COPY: EDITING_OPERATIONS.COPY,
    PASTE: EDITING_OPERATIONS.PASTE,

    ...GRID_PATTERNS.createGridNavigation(gridScope),

    HELP: UNIVERSAL_PATTERNS.HELP,
    SEARCH: UNIVERSAL_PATTERNS.SEARCH,
  };

  dataAppPatternsCache.set(gridScope, patterns);
  return patterns;
}
